import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BookOpen, HeartHandshake, Sparkles, Quote, Copy, Download, RotateCcw, Send, Flame, ScrollText, History, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { currentLang } from "@/lib/lang";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { Overline } from "@/components/Bits";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const MOOD_CHIPS = [
  "Anxious", "Grateful", "Discouraged", "Joyful", "Weary",
  "Broken", "Hopeful", "Angry", "Lonely", "Confused", "At peace", "Overwhelmed",
];

function copy(text) {
  try {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard.");
  } catch {
    toast.error("Copy failed — please select and copy manually.");
  }
}

function downloadText(filename, text) {
  const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Sermon Outline Generator
// ---------------------------------------------------------------------------
function SermonStudio() {
  const empty = { topic: "", scripture: "", audience: "Kingdom leaders", style: "Expository" };
  const [form, setForm] = useState(empty);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.topic.trim().length < 3) {
      toast.error("Please share a topic to work from.");
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/ai/sermon-outline", { ...form, language: currentLang() });
      setResult(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const outlineAsText = () => {
    if (!result) return "";
    const lines = [
      `SERMON OUTLINE — ${result.title}`,
      `\nBIG IDEA: ${result.big_idea}`,
      `KEY SCRIPTURE: ${result.key_scripture}`,
      `\nINTRODUCTION\n${result.introduction}`,
      "\n---",
    ];
    (result.points || []).forEach((p, i) => {
      lines.push(`\nPOINT ${i + 1}: ${p.heading}`);
      lines.push(`Scripture: ${p.scripture}`);
      lines.push(`Explanation: ${p.explanation}`);
      lines.push(`Illustration: ${p.illustration}`);
      lines.push(`Application: ${p.application}`);
    });
    lines.push(`\nCALL TO ACTION\n${result.call_to_action}`);
    lines.push(`\nCLOSING PRAYER\n${result.closing_prayer}`);
    return lines.join("\n");
  };

  return (
    <div className="grid gap-10 lg:grid-cols-[420px_1fr]" data-testid="sermon-studio">
      {/* Form */}
      <div className="lg:sticky lg:top-28 border border-gold-500/25 bg-ink-paper/40 p-7 h-fit">
        <div className="flex items-center gap-3 mb-1">
          <BookOpen size={20} className="text-gold-500" />
          <Overline>Sermon Studio</Overline>
        </div>
        <p className="text-sm text-white/60 mt-3 mb-6">
          Give the Spirit a spark — a topic or a passage — and receive a preachable 3-point outline in seconds.
        </p>
        <form onSubmit={submit} className="space-y-4" data-testid="sermon-form">
          <div>
            <label className="block text-[11px] uppercase tracking-[0.2em] text-white/55 mb-2">Topic or theme *</label>
            <Input
              required
              value={form.topic}
              onChange={(e) => setForm({ ...form, topic: e.target.value })}
              placeholder="e.g. Walking in Kingdom Authority"
              data-testid="sermon-topic"
              className="bg-ink border-gold-500/25 text-white placeholder:text-white/40"
            />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-[0.2em] text-white/55 mb-2">Anchor scripture (optional)</label>
            <Input
              value={form.scripture}
              onChange={(e) => setForm({ ...form, scripture: e.target.value })}
              placeholder="e.g. Luke 10:19"
              data-testid="sermon-scripture"
              className="bg-ink border-gold-500/25 text-white placeholder:text-white/40"
            />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-[0.2em] text-white/55 mb-2">Audience</label>
            <select
              value={form.audience}
              onChange={(e) => setForm({ ...form, audience: e.target.value })}
              data-testid="sermon-audience"
              className="w-full bg-ink border border-gold-500/25 text-white/90 px-3 py-2 text-sm outline-none focus:border-gold-400"
            >
              {["General congregation", "Kingdom leaders", "Youth", "Marketplace believers", "New believers", "Intercessors"].map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-[0.2em] text-white/55 mb-2">Style</label>
            <select
              value={form.style}
              onChange={(e) => setForm({ ...form, style: e.target.value })}
              data-testid="sermon-style"
              className="w-full bg-ink border border-gold-500/25 text-white/90 px-3 py-2 text-sm outline-none focus:border-gold-400"
            >
              {["Expository", "Topical", "Narrative", "Prophetic", "Teaching"].map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
          </div>
          <Button
            type="submit"
            disabled={busy}
            data-testid="sermon-submit"
            className="w-full bg-gold-500 text-ink hover:bg-gold-400 font-semibold"
          >
            <Sparkles size={15} className="mr-2" />
            {busy ? "Prayerfully drafting…" : "Generate outline"}
          </Button>
          {result && (
            <button
              type="button"
              onClick={() => { setResult(null); setForm(empty); }}
              data-testid="sermon-reset"
              className="w-full inline-flex items-center justify-center gap-1.5 text-[11px] uppercase tracking-[0.2em] text-gold-400 hover:text-gold-300 pt-2"
            >
              <RotateCcw size={12} /> Start over
            </button>
          )}
        </form>
      </div>

      {/* Result */}
      <div>
        {busy ? (
          <div className="min-h-[500px] border border-gold-500/15 flex flex-col items-center justify-center text-center p-10" data-testid="sermon-busy">
            <div className="animate-pulse">
              <Sparkles size={40} className="text-gold-500 mb-4" />
            </div>
            <p className="font-serif text-2xl text-white">Waiting on the Word…</p>
            <p className="mt-2 text-sm text-white/50">Drafting a Spirit-led outline. This usually takes 10–20 seconds.</p>
          </div>
        ) : result ? (
          <motion.article
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="border border-gold-500/25 bg-ink-paper/40 p-8 md:p-10"
            data-testid="sermon-result"
          >
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <p className="text-[11px] uppercase tracking-[0.25em] text-gold-400">{result.key_scripture}</p>
                <h2 className="mt-2 font-serif text-3xl md:text-4xl leading-tight text-white" data-testid="sermon-title">{result.title}</h2>
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => copy(outlineAsText())} data-testid="sermon-copy" className="inline-flex items-center gap-1.5 border border-gold-500/30 px-3 py-2 text-[11px] uppercase tracking-[0.15em] text-white/70 hover:text-gold-300">
                  <Copy size={12} /> Copy
                </button>
                <button onClick={() => downloadText(`sermon-${result.title.slice(0,40).replace(/\s+/g,'-').toLowerCase()}.txt`, outlineAsText())} data-testid="sermon-download" className="inline-flex items-center gap-1.5 border border-gold-500/30 px-3 py-2 text-[11px] uppercase tracking-[0.15em] text-white/70 hover:text-gold-300">
                  <Download size={12} /> .txt
                </button>
                <button
                  onClick={async () => {
                    try {
                      const res = await api.post("/ai/sermon-outline/pdf", result, { responseType: "blob" });
                      const url = URL.createObjectURL(res.data);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = `sermon-${result.title.slice(0,40).replace(/\s+/g,'-').toLowerCase()}.pdf`;
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                      URL.revokeObjectURL(url);
                    } catch {
                      toast.error("Could not generate PDF. Please try again.");
                    }
                  }}
                  data-testid="sermon-download-pdf"
                  className="inline-flex items-center gap-1.5 bg-gold-500 text-ink px-3 py-2 text-[11px] uppercase tracking-[0.15em] font-semibold hover:bg-gold-400"
                >
                  <Download size={12} /> PDF
                </button>
              </div>
            </div>

            <div className="mt-6 border-l-2 border-gold-500/60 pl-6">
              <p className="text-[11px] uppercase tracking-[0.2em] text-gold-400 mb-1">Big idea</p>
              <p className="font-accent italic text-lg text-white/90">{result.big_idea}</p>
            </div>

            <section className="mt-8">
              <p className="text-[11px] uppercase tracking-[0.25em] text-gold-400 mb-2">Introduction</p>
              <p className="text-white/80 leading-relaxed">{result.introduction}</p>
            </section>

            <section className="mt-10 space-y-8">
              {(result.points || []).map((p, i) => (
                <div key={i} className="border border-gold-500/15 bg-ink/40 p-6 md:p-7" data-testid={`sermon-point-${i}`}>
                  <div className="flex items-baseline gap-4">
                    <span className="font-serif text-4xl text-gold-500/70">{i + 1}.</span>
                    <div>
                      <h3 className="font-serif text-2xl text-white leading-tight">{p.heading}</h3>
                      <p className="text-xs text-gold-400 mt-1">{p.scripture}</p>
                    </div>
                  </div>
                  <div className="mt-5 space-y-4 text-sm md:text-[15px] text-white/80 leading-relaxed">
                    <p><span className="text-[10px] uppercase tracking-[0.25em] text-gold-400 mr-2">Explanation</span>{p.explanation}</p>
                    <p><span className="text-[10px] uppercase tracking-[0.25em] text-gold-400 mr-2">Illustration</span>{p.illustration}</p>
                    <p><span className="text-[10px] uppercase tracking-[0.25em] text-gold-400 mr-2">Application</span>{p.application}</p>
                  </div>
                </div>
              ))}
            </section>

            <section className="mt-10">
              <p className="text-[11px] uppercase tracking-[0.25em] text-gold-400 mb-2">Call to action</p>
              <p className="text-white/85 leading-relaxed">{result.call_to_action}</p>
            </section>

            <div className="mt-10 border border-gold-500/25 bg-ink p-6 md:p-7" data-testid="sermon-closing-prayer">
              <div className="flex items-center gap-2 mb-3">
                <Quote size={14} className="text-gold-500" />
                <p className="text-[11px] uppercase tracking-[0.25em] text-gold-400">Closing prayer</p>
              </div>
              <p className="font-accent italic text-lg text-white/85 leading-relaxed">{result.closing_prayer}</p>
            </div>
          </motion.article>
        ) : (
          <div className="min-h-[500px] border border-dashed border-gold-500/20 flex flex-col items-center justify-center text-center p-10" data-testid="sermon-empty">
            <BookOpen size={40} className="text-gold-500/60 mb-4" strokeWidth={1.4} />
            <p className="font-serif text-2xl text-white">Your outline appears here</p>
            <p className="mt-2 text-sm text-white/50 max-w-md">Enter a topic, an optional scripture, choose your audience, and the studio will draft a 3-point outline with introduction, illustrations, applications, and a closing prayer.</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Mood Responsive Guide
// ---------------------------------------------------------------------------
function MoodGuide() {
  const [mood, setMood] = useState("");
  const [context, setContext] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const isLoggedIn = typeof window !== "undefined" && !!localStorage.getItem("abj_token");

  const loadHistory = async () => {
    if (!isLoggedIn) return;
    try {
      const { data } = await api.get("/ai/mood-guide/history?limit=15");
      setHistory(Array.isArray(data) ? data : []);
    } catch {
      /* silent */
    }
  };

  useEffect(() => { loadHistory(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const clearHistory = async () => {
    if (!window.confirm("Clear all your saved mood conversations? This can't be undone.")) return;
    try {
      await api.delete("/ai/mood-guide/history");
      setHistory([]);
      toast.success("Your mood history has been cleared.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (mood.trim().length < 2) {
      toast.error("Please choose or type how you're feeling.");
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/ai/mood-guide", { mood: mood.trim(), context: context.trim(), language: currentLang() });
      setResult(data);
      if (isLoggedIn) loadHistory();  // refresh history so the newest entry shows
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid gap-10 lg:grid-cols-[420px_1fr]" data-testid="mood-guide">
      {/* Form */}
      <div className="lg:sticky lg:top-28 border border-gold-500/25 bg-ink-paper/40 p-7 h-fit">
        <div className="flex items-center gap-3 mb-1">
          <HeartHandshake size={20} className="text-gold-500" />
          <Overline>How are you today?</Overline>
        </div>
        <p className="text-sm text-white/60 mt-3 mb-6">
          Tell us how you're feeling. In seconds, receive a Spirit-led scripture, a word of encouragement, and a prayer written just for you.
        </p>
        <form onSubmit={submit} className="space-y-5" data-testid="mood-form">
          <div>
            <label className="block text-[11px] uppercase tracking-[0.2em] text-white/55 mb-3">Pick a mood…</label>
            <div className="flex flex-wrap gap-2" data-testid="mood-chips">
              {MOOD_CHIPS.map((m) => (
                <button
                  type="button"
                  key={m}
                  onClick={() => setMood(m)}
                  data-testid={`mood-chip-${m.toLowerCase().replace(/\s+/g,'-')}`}
                  className={`px-3 py-1.5 text-xs uppercase tracking-[0.12em] border transition-colors duration-300 ${
                    mood.toLowerCase() === m.toLowerCase()
                      ? "border-gold-400 bg-gold-500/15 text-gold-300"
                      : "border-white/15 text-white/60 hover:text-white"
                  }`}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-[0.2em] text-white/55 mb-2">…or type your own</label>
            <Input
              value={mood}
              onChange={(e) => setMood(e.target.value)}
              placeholder="e.g. anxious, thankful, restless…"
              data-testid="mood-input"
              className="bg-ink border-gold-500/25 text-white placeholder:text-white/40"
            />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-[0.2em] text-white/55 mb-2">A little context (optional)</label>
            <Textarea
              rows={3}
              maxLength={500}
              value={context}
              onChange={(e) => setContext(e.target.value)}
              placeholder="Share as much or as little as you'd like…"
              data-testid="mood-context"
              className="bg-ink border-gold-500/25 text-white placeholder:text-white/40 resize-none"
            />
          </div>
          <Button
            type="submit"
            disabled={busy}
            data-testid="mood-submit"
            className="w-full bg-gold-500 text-ink hover:bg-gold-400 font-semibold"
          >
            <Sparkles size={15} className="mr-2" />
            {busy ? "Listening…" : "Speak to my heart"}
          </Button>
          {result && (
            <button
              type="button"
              onClick={() => { setResult(null); setMood(""); setContext(""); }}
              data-testid="mood-reset"
              className="w-full inline-flex items-center justify-center gap-1.5 text-[11px] uppercase tracking-[0.2em] text-gold-400 hover:text-gold-300 pt-2"
            >
              <RotateCcw size={12} /> Ask again
            </button>
          )}
        </form>

        {/* Past conversations — only for logged-in believers */}
        {isLoggedIn && history.length > 0 && (
          <div className="mt-8 border-t border-gold-500/15 pt-6" data-testid="mood-history">
            <button
              type="button"
              onClick={() => setShowHistory((v) => !v)}
              data-testid="mood-history-toggle"
              className="w-full flex items-center justify-between text-[11px] uppercase tracking-[0.22em] text-gold-400 hover:text-gold-300 transition-colors"
            >
              <span className="inline-flex items-center gap-2">
                <History size={13} /> Your past conversations · {history.length}
              </span>
              <span className="text-white/40">{showHistory ? "Hide" : "Show"}</span>
            </button>
            <AnimatePresence>
              {showHistory && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="mt-4 space-y-2 max-h-[360px] overflow-y-auto pr-1"
                >
                  {history.map((h) => (
                    <button
                      type="button"
                      key={h.id}
                      onClick={() => {
                        // Replay this past conversation as the current result
                        setResult({
                          affirmation: h.encouragement?.split(/\n+/)[0]?.slice(0, 200) || "A word from the Lord for you.",
                          scripture_reference: h.reference || "",
                          scripture_text: h.scripture || "",
                          encouragement: h.encouragement || "",
                          prayer: h.prayer || "",
                          affirmations: [],
                        });
                        setMood(h.mood || "");
                        setContext(h.context || "");
                        toast.success("Loaded past word from your journal.");
                      }}
                      data-testid="mood-history-item"
                      className="w-full text-left border border-white/10 hover:border-gold-500/50 bg-ink/40 hover:bg-ink px-3 py-2 transition-colors group"
                    >
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-[10px] uppercase tracking-[0.2em] text-gold-400 font-semibold">
                          {h.mood}
                        </span>
                        <span className="text-[9px] text-white/40 ml-auto">
                          {h.created_at ? new Date(h.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : ""}
                        </span>
                      </div>
                      <p className="text-xs text-white/60 line-clamp-2 group-hover:text-white/80 transition-colors">
                        {h.reference || h.encouragement?.slice(0, 90) || "…"}
                      </p>
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={clearHistory}
                    data-testid="mood-history-clear"
                    className="w-full inline-flex items-center justify-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-red-400/70 hover:text-red-300 py-2 mt-2"
                  >
                    <Trash2 size={11} /> Clear all history
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </div>

      {/* Result */}
      <div>
        {busy ? (
          <div className="min-h-[500px] border border-gold-500/15 flex flex-col items-center justify-center text-center p-10" data-testid="mood-busy">
            <div className="animate-pulse">
              <HeartHandshake size={40} className="text-gold-500 mb-4" />
            </div>
            <p className="font-serif text-2xl text-white">Bringing this before the Lord…</p>
          </div>
        ) : result ? (
          <motion.article
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="border border-gold-500/25 bg-ink-paper/40 p-8 md:p-10"
            data-testid="mood-result"
          >
            <div className="flex items-center gap-3">
              <HeartHandshake size={18} className="text-gold-500" />
              <p className="text-[11px] uppercase tracking-[0.25em] text-gold-400">A word for you</p>
            </div>
            <p className="mt-4 font-serif text-2xl md:text-3xl text-white leading-tight" data-testid="mood-affirmation">
              {result.affirmation}
            </p>

            <div className="mt-8 border-l-2 border-gold-500/60 pl-6">
              <p className="text-[11px] uppercase tracking-[0.2em] text-gold-400 mb-2">{result.scripture_reference}</p>
              <p className="font-accent italic text-xl md:text-2xl text-white/90 leading-snug">"{result.scripture_text}"</p>
            </div>

            <div className="mt-8 space-y-4 text-white/80 leading-relaxed md:text-[16px]" data-testid="mood-encouragement">
              {result.encouragement.split(/\n+/).map((p, i) => <p key={i}>{p.trim()}</p>)}
            </div>

            <div className="mt-8 border border-gold-500/25 bg-ink p-6" data-testid="mood-prayer">
              <div className="flex items-center gap-2 mb-3">
                <Quote size={14} className="text-gold-500" />
                <p className="text-[11px] uppercase tracking-[0.25em] text-gold-400">Pray with me</p>
              </div>
              <p className="font-accent italic text-lg text-white/85 leading-relaxed">{result.prayer}</p>
            </div>

            {result.affirmations?.length > 0 && (
              <div className="mt-8" data-testid="mood-affirmations">
                <p className="text-[11px] uppercase tracking-[0.25em] text-gold-400 mb-4">Declarations for today</p>
                <ul className="space-y-2">
                  {result.affirmations.map((a, i) => (
                    <li key={i} className="border border-gold-500/15 bg-ink/40 px-5 py-3 text-white/85">
                      <span className="text-gold-500 mr-3">◆</span>{a}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </motion.article>
        ) : (
          <div className="min-h-[500px] border border-dashed border-gold-500/20 flex flex-col items-center justify-center text-center p-10" data-testid="mood-empty">
            <HeartHandshake size={40} className="text-gold-500/60 mb-4" strokeWidth={1.4} />
            <p className="font-serif text-2xl text-white">A word tailored to your heart</p>
            <p className="mt-2 text-sm text-white/50 max-w-md">Pick how you're feeling, and the guide will draft a scripture, a pastoral encouragement, a prayer, and 3 declarations you can speak today.</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Renewal of Mind (Romans 12:1-2)
// ---------------------------------------------------------------------------
const RENEWAL_CHIPS = [
  "Fear of failure", "Poverty mindset", "Rejection wounds", "Unforgiveness",
  "Anxiety about the future", "Feeling not enough", "Comparison", "Shame from the past",
  "Doubt about my calling", "Fear of man", "Bitterness", "Lust / temptation",
];

function RenewalOfMind() {
  const [area, setArea] = useState("");
  const [context, setContext] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (area.trim().length < 3) {
      toast.error("Please name the area where you want your mind renewed.");
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/ai/renewal-of-mind", { area: area.trim(), context: context.trim(), language: currentLang() });
      setResult(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid gap-10 lg:grid-cols-[420px_1fr]" data-testid="renewal-of-mind">
      {/* Form */}
      <div className="lg:sticky lg:top-28 border border-gold-500/25 bg-ink-paper/40 p-7 h-fit">
        <div className="flex items-center gap-3 mb-1">
          <Flame size={20} className="text-gold-500" />
          <Overline>Renewal of Mind</Overline>
        </div>
        <p className="text-[11px] uppercase tracking-[0.3em] text-gold-400 mt-3 mb-2 font-semibold">
          Clean Your Mind · Transform Your Life
        </p>
        <p className="text-sm text-white/60 mb-4">
          Rooted in <span className="text-gold-300">Romans 12:1–2</span> — the Apostle's foundational text. Name a lie or worldly pattern, and receive the Kingdom truth, an anchor scripture, 7 declarations, a daily practice, and a surrender prayer.
        </p>
        <p className="italic font-accent text-white/50 text-sm leading-relaxed border-l-2 border-gold-500/40 pl-4 mb-6">
          "Be not conformed to this world: but be ye transformed by the renewing of your mind…"
        </p>
        <form onSubmit={submit} className="space-y-5" data-testid="renewal-form">
          <div>
            <label className="block text-[11px] uppercase tracking-[0.2em] text-white/55 mb-3">Common mindset areas</label>
            <div className="flex flex-wrap gap-2" data-testid="renewal-chips">
              {RENEWAL_CHIPS.map((m) => (
                <button
                  type="button"
                  key={m}
                  onClick={() => setArea(m)}
                  data-testid={`renewal-chip-${m.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}
                  className={`px-3 py-1.5 text-xs uppercase tracking-[0.12em] border transition-colors duration-300 ${
                    area.toLowerCase() === m.toLowerCase()
                      ? "border-gold-400 bg-gold-500/15 text-gold-300"
                      : "border-white/15 text-white/60 hover:text-white"
                  }`}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-[0.2em] text-white/55 mb-2">…or name your own area *</label>
            <Input
              value={area}
              onChange={(e) => setArea(e.target.value)}
              placeholder="e.g. Insecurity about my leadership"
              data-testid="renewal-area"
              className="bg-ink border-gold-500/25 text-white placeholder:text-white/40"
            />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-[0.2em] text-white/55 mb-2">A little context (optional)</label>
            <Textarea
              rows={3}
              maxLength={600}
              value={context}
              onChange={(e) => setContext(e.target.value)}
              placeholder="Where does this show up in your life?"
              data-testid="renewal-context"
              className="bg-ink border-gold-500/25 text-white placeholder:text-white/40 resize-none"
            />
          </div>
          <Button
            type="submit"
            disabled={busy}
            data-testid="renewal-submit"
            className="w-full bg-gold-500 text-ink hover:bg-gold-400 font-semibold"
          >
            <Flame size={15} className="mr-2" />
            {busy ? "Speaking the truth…" : "Renew my mind"}
          </Button>
          {result && (
            <button
              type="button"
              onClick={() => { setResult(null); setArea(""); setContext(""); }}
              data-testid="renewal-reset"
              className="w-full inline-flex items-center justify-center gap-1.5 text-[11px] uppercase tracking-[0.2em] text-gold-400 hover:text-gold-300 pt-2"
            >
              <RotateCcw size={12} /> Start over
            </button>
          )}
        </form>
      </div>

      {/* Result */}
      <div>
        {busy ? (
          <div className="min-h-[500px] border border-gold-500/15 flex flex-col items-center justify-center text-center p-10" data-testid="renewal-busy">
            <div className="animate-pulse">
              <Flame size={40} className="text-gold-500 mb-4" />
            </div>
            <p className="font-serif text-2xl text-white">Renewing by the Word…</p>
          </div>
        ) : result ? (
          <motion.article
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="border border-gold-500/25 bg-ink-paper/40 p-8 md:p-10 space-y-8"
            data-testid="renewal-result"
          >
            <div>
              <div className="flex items-center gap-3">
                <Flame size={18} className="text-gold-500" />
                <p className="text-[11px] uppercase tracking-[0.25em] text-gold-400">Renewal of Mind</p>
              </div>
              <h2 className="mt-3 font-serif text-3xl md:text-4xl text-white leading-tight" data-testid="renewal-area-title">
                {result.area}
              </h2>
            </div>

            <div className="border border-red-500/20 bg-red-500/5 p-6" data-testid="renewal-old">
              <p className="text-[11px] uppercase tracking-[0.25em] text-red-300 mb-3">The old pattern (Romans 12:2a)</p>
              <p className="text-white/80 leading-relaxed">{result.the_old_pattern}</p>
            </div>

            <div className="border border-gold-500/25 bg-gold-500/5 p-6" data-testid="renewal-truth">
              <p className="text-[11px] uppercase tracking-[0.25em] text-gold-400 mb-3">The Kingdom truth (Romans 12:2b)</p>
              <p className="text-white/90 leading-relaxed md:text-[16px]">{result.the_kingdom_truth}</p>
            </div>

            {result.anchor_scripture && (
              <div className="border-l-2 border-gold-500/60 pl-6" data-testid="renewal-anchor">
                <p className="text-[11px] uppercase tracking-[0.25em] text-gold-400 mb-2">{result.anchor_scripture.reference}</p>
                <p className="font-accent italic text-xl md:text-2xl text-white leading-snug">"{result.anchor_scripture.text}"</p>
                <p className="mt-3 text-sm text-white/60">{result.anchor_scripture.why}</p>
              </div>
            )}

            <div data-testid="renewal-declarations">
              <p className="text-[11px] uppercase tracking-[0.25em] text-gold-400 mb-4">Seven declarations · speak daily</p>
              <ul className="space-y-2">
                {result.declarations?.map((d, i) => (
                  <li key={i} className="border border-gold-500/15 bg-ink/40 px-5 py-3 text-white/90 flex items-baseline gap-3">
                    <span className="text-gold-500 text-xs font-bold shrink-0">{String(i+1).padStart(2,"0")}</span>
                    <span>{d}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="border border-gold-500/20 bg-ink p-6" data-testid="renewal-practice">
              <p className="text-[11px] uppercase tracking-[0.25em] text-gold-400 mb-3">Daily practice this week</p>
              <p className="text-white/85 leading-relaxed">{result.daily_practice}</p>
            </div>

            <div className="border border-gold-500/25 bg-ink p-6" data-testid="renewal-prayer">
              <div className="flex items-center gap-2 mb-3">
                <Quote size={14} className="text-gold-500" />
                <p className="text-[11px] uppercase tracking-[0.25em] text-gold-400">Prayer of surrender (Romans 12:1)</p>
              </div>
              <p className="font-accent italic text-lg text-white/85 leading-relaxed">{result.surrender_prayer}</p>
            </div>
          </motion.article>
        ) : (
          <div className="min-h-[500px] border border-dashed border-gold-500/20 flex flex-col items-center justify-center text-center p-10" data-testid="renewal-empty">
            <ScrollText size={40} className="text-gold-500/60 mb-4" strokeWidth={1.4} />
            <p className="font-serif text-2xl text-white">The Word will renew your mind</p>
            <p className="mt-2 text-sm text-white/50 max-w-md">Name a lie or worldly pattern you're battling. Receive the Kingdom truth, an anchor scripture, 7 first-person declarations, a daily practice, and a surrender prayer — all in the Apostle's voice.</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Studio (unified)
// ---------------------------------------------------------------------------
export default function Studio() {
  return (
    <div data-testid="studio-page">
      <PageHero
        overline="Kingdom Studio · Anchored in Romans 12:1–2"
        title={<>Clean your mind. <span className="font-accent text-gold-gradient italic">Transform your life.</span></>}
        subtitle="Three Spirit-led companions, one foundation: 'Be ye transformed by the renewing of your mind.' Draft sermon outlines. Renew a mindset. Receive a word for your heart. All in Apostle Blaise Judah's voice, powered by Claude Sonnet 4.5."
      />

      <section className="mx-auto max-w-[1400px] px-6 md:px-10 pb-28">
        <Tabs defaultValue="sermon" className="w-full">
          <TabsList className="bg-ink-paper border border-gold-500/20 mb-10 mx-auto flex w-fit" data-testid="studio-tabs">
            <TabsTrigger value="sermon" data-testid="studio-tab-sermon" className="data-[state=active]:bg-gold-500 data-[state=active]:text-ink text-white/70 px-5">
              <BookOpen size={14} className="mr-2" /> Sermon
            </TabsTrigger>
            <TabsTrigger value="renewal" data-testid="studio-tab-renewal" className="data-[state=active]:bg-gold-500 data-[state=active]:text-ink text-white/70 px-5">
              <Flame size={14} className="mr-2" /> Renewal of Mind
            </TabsTrigger>
            <TabsTrigger value="mood" data-testid="studio-tab-mood" className="data-[state=active]:bg-gold-500 data-[state=active]:text-ink text-white/70 px-5">
              <HeartHandshake size={14} className="mr-2" /> Mood Guide
            </TabsTrigger>
          </TabsList>

          <TabsContent value="sermon">
            <Reveal><SermonStudio /></Reveal>
          </TabsContent>
          <TabsContent value="renewal">
            <Reveal><RenewalOfMind /></Reveal>
          </TabsContent>
          <TabsContent value="mood">
            <Reveal><MoodGuide /></Reveal>
          </TabsContent>
        </Tabs>
      </section>
    </div>
  );
}
